import { useEffect, useRef, useState } from 'react';
import { solutionsData } from '@/mocks/homeData';

export default function SolutionsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const currentSolution = solutionsData[activeTab];

  return (
    <section
      id="solutions"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-firmai-darker overflow-hidden"
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {isVisible && (
          <>
            <div className="absolute top-20 left-[10%] w-2 h-2 bg-firmai-green/30 rounded-full animate-float-slow" />
            <div className="absolute top-40 left-[25%] w-1.5 h-1.5 bg-firmai-blue/30 rounded-full animate-float-slow" style={{ animationDelay: '1s' }} />
            <div className="absolute top-60 left-[15%] w-1 h-1 bg-firmai-green/20 rounded-full animate-float-slow" style={{ animationDelay: '2s' }} />
            <div className="absolute top-32 right-[20%] w-2 h-2 bg-firmai-blue/20 rounded-full animate-float-slow" style={{ animationDelay: '1.5s' }} />
            <div className="absolute top-48 right-[10%] w-1.5 h-1.5 bg-firmai-green/25 rounded-full animate-float-slow" style={{ animationDelay: '0.5s' }} />
          </>
        )}
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        {/* Section Header */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-12 md:mb-16">
          <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">
            应用场景
          </span>
          <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-3">
            工业企业与园区数字化能碳管理
          </h2>
          <p className="text-firmai-text mt-4 max-w-2xl mx-auto text-sm md:text-base">
            覆盖工业企业独立建设、工业园区统一管理与 CCER 碳资产开发三大核心场景，满足工信部数字化能碳管理中心建设要求
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 max-w-4xl mx-auto mb-10">
          <div className="flex flex-col sm:flex-row items-stretch gap-3 p-1.5 bg-firmai-dark/80 border border-firmai-border rounded-xl">
            {solutionsData.map((solution, index) => (
              <button
                key={solution.id}
                onClick={() => setActiveTab(index)}
                className={`relative flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  activeTab === index
                    ? 'text-firmai-dark bg-firmai-green'
                    : 'text-firmai-text hover:text-white'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${solution.icon} text-base`} />
                </span>
                {solution.title}
                {activeTab === index && (
                  <span className="absolute inset-0 rounded-lg bg-firmai-green animate-pulse-soft" style={{ zIndex: -1 }} />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Solution Content */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-200 max-w-5xl mx-auto">
          <div className="bg-firmai-card border border-firmai-border rounded-xl overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              {/* Left - Info */}
              <div className="w-full lg:w-[55%] p-6 md:p-10 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-firmai-green/20 to-firmai-blue/20 rounded-xl border border-firmai-green/20">
                    <span className="w-6 h-6 flex items-center justify-center text-firmai-green">
                      <i className={`${currentSolution.icon} text-xl`} />
                    </span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg md:text-xl">
                      {currentSolution.title}
                    </h3>
                    <p className="text-firmai-green text-xs font-medium">
                      {currentSolution.subtitle}
                    </p>
                  </div>
                </div>

                <p className="text-firmai-text text-sm md:text-base leading-relaxed">
                  {currentSolution.description}
                </p>

                {/* Features List */}
                <div className="space-y-3">
                  {currentSolution.features.map((feature, idx) => (
                    <div
                      key={feature}
                      className="flex items-start gap-3 group"
                      style={{ animationDelay: `${idx * 80}ms` }}
                    >
                      <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 text-firmai-green group-hover:scale-110 transition-transform duration-200">
                        <i className="ri-check-line" />
                      </span>
                      <span className="text-firmai-text text-sm group-hover:text-white transition-colors duration-200">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="pt-2">
                  <a
                    href="#demo"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 transition-all duration-200 whitespace-nowrap group"
                  >
                    了解详情
                    <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </a>
                </div>
              </div>

              {/* Right - Visual */}
              <div className="w-full lg:w-[45%] relative min-h-[280px] lg:min-h-0">
                <img
                  src={currentSolution.image}
                  alt={currentSolution.title}
                  className="w-full h-full object-cover min-h-[280px] lg:min-h-full"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-firmai-card via-firmai-card/50 to-transparent lg:bg-gradient-to-l" />

                {/* Floating stat card */}
                <div className="absolute bottom-4 right-4 bg-firmai-card/90 backdrop-blur-sm border border-firmai-green/30 rounded-lg p-3 shadow-lg animate-float">
                  <div className="text-firmai-green font-bold text-lg">
                    {currentSolution.statValue}
                  </div>
                  <div className="text-firmai-muted text-xs">
                    {currentSolution.statLabel}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats Row */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-300 grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto">
          {[
            { value: '300+', label: '工业企业落地' },
            { value: '50+', label: '工业园区项目' },
            { value: '12项', label: '建设指南功能全覆盖' },
            { value: '98%', label: '客户建设通过率' },
          ].map((stat, index) => (
            <div
              key={stat.label}
              className="text-center p-4 bg-firmai-dark/50 border border-firmai-border rounded-lg hover:border-firmai-green/30 transition-all duration-300 group"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="text-firmai-green font-display font-bold text-xl md:text-2xl group-hover:scale-105 transition-transform duration-300">
                {stat.value}
              </div>
              <div className="text-firmai-muted text-xs mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        @keyframes pulse-soft {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .animate-pulse-soft {
          animation: pulse-soft 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}